import { Injectable, inject } from '@angular/core';
import { GalleryDto, GalleryPublicAccessLevel, GalleryTenantAccessLevel } from '@nusign/picflow-backend-types';
import { Observable, firstValueFrom, switchMap } from 'rxjs';
import { GalleryAccessLevel } from '../lib/access-level';
import { GalleryAccessLevelSubService } from './gallery-access-level-sub.service';
import { GalleryService } from './gallery.service';

@Injectable({
  providedIn: 'root',
})
export class GalleryAccessLevelService {
  private galleryService = inject(GalleryService);
  private galleryAccessLevelSubService = inject(GalleryAccessLevelSubService);

  public async getAccessLevel(
    gallery: GalleryDto,
    options: { ignorePublicAccessLevel?: boolean } = {}
  ): Promise<GalleryAccessLevel> {
    const galleries = await firstValueFrom(this.galleryService.getSharedWithMeGalleries());
    return this.galleryAccessLevelSubService.getAccessLevel(gallery, galleries, options);
  }

  public async getAccessLevels(gallery: GalleryDto): Promise<[GalleryTenantAccessLevel, GalleryPublicAccessLevel]> {
    return this.galleryAccessLevelSubService.getAccessLevels(gallery);
  }

  public isGalleryGuest(gallery: GalleryDto): Observable<boolean> {
    return this.galleryService
      .getSharedWithMeGalleries()
      .pipe(switchMap(galleries => this.galleryAccessLevelSubService.isGalleryGuest(gallery, galleries)));
  }

  public getMemberActions(myRole: string, memberRole: string, memberCount: number, memberIsCurrentUser: boolean) {
    const actions: string[] = [];
    if (this.canRemove(myRole, memberRole, memberIsCurrentUser)) actions.push('remove');
    if (this.canLeave(memberRole, memberIsCurrentUser)) actions.push('leave');
    if (this.canChangeRole(myRole, memberRole)) actions.push('changeRole');
    if (this.canTransferOwnership(myRole, memberRole, memberCount)) actions.push('transferOwnership');
    return actions;
  }

  private canChangeRole(myRole: string, memberRole: string) {
    return ['admin', 'owner'].includes(myRole) && memberRole !== 'owner';
  }

  private canTransferOwnership(myRole: string, memberRole: string, memberCount: number) {
    if (memberCount === 1) return false;
    return myRole === 'owner' && memberRole === 'owner';
  }

  private canRemove(myRole: string, memberRole: string, memberIsCurrentUser: boolean) {
    if (memberRole === 'owner') return false;
    if (!['admin', 'owner'].includes(myRole)) return false;
    return !memberIsCurrentUser;
  }

  private canLeave(memberRole: string, memberIsCurrentUser: boolean) {
    if (memberRole === 'owner') return false;
    if (memberRole === 'member') return false;
    return memberIsCurrentUser;
  }
}
